import { Box, Button, Flex, Icon, Input } from '@welbe/design-system';
import { Show, Spinner, useFieldContext } from '@chakra-ui/react';
import { forwardRef, useMemo, useRef, useState } from 'react';

interface SelectOption {
	id: string;
	name: string;
}

interface CustomSelectProps {
	loading?: boolean;
	disabled?: boolean;
	invalid?: boolean;
	readOnly?: boolean;
	required?: boolean;
	options?: SelectOption[];
	placeholder?: string;
	value?: string;
	onChange?: (value: string) => void;
	name?: string;
	onBlur?: () => void;
}

const normalize = (str: string) =>
	str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();

export const CustomSelect = forwardRef<HTMLButtonElement, CustomSelectProps>(
	(
		{
			options = [],
			loading,
			disabled,
			invalid,
			readOnly,
			required,
			placeholder = 'Selecciona una opción',
			value = '',
			onChange,
			name,
			onBlur: onBlurProp,
		},
		ref,
	): JSX.Element => {
		const fieldContext = useFieldContext();

		const isDisabled = disabled ?? fieldContext?.disabled ?? false;
		const isInvalid = invalid ?? fieldContext?.invalid ?? false;
		const isReadOnly = readOnly ?? fieldContext?.readOnly ?? false;
		const isRequired = required ?? fieldContext?.required ?? false;

		const [isOpen, setIsOpen] = useState(false);
		const [searchTerm, setSearchTerm] = useState('');
		const containerRef = useRef<HTMLDivElement>(null);

		const filteredOptions = useMemo(() => {
			if (!searchTerm) return options;
			const normalizedSearch = normalize(searchTerm);
			return options.filter((option) =>
				normalize(option.name).includes(normalizedSearch),
			);
		}, [options, searchTerm]);

		const selectedLabel = options.find((opt) => opt.id === value)?.name;

		const handleSelect = (id: string) => {
			if (isReadOnly) return;
			onChange?.(id);
			setIsOpen(false);
			setSearchTerm('');
		};

		const handleToggle = () => {
			if (isReadOnly) return;
			setIsOpen((prev) => !prev);
		};

		const handleBlur = (e: React.FocusEvent) => {
			if (!containerRef.current?.contains(e.relatedTarget)) {
				setIsOpen(false);
				setSearchTerm('');
				onBlurProp?.();
			}
		};

		return (
			<div
				ref={containerRef}
				onBlur={handleBlur}
				style={{ position: 'relative', width: '100%' }}
			>
				<Button
					ref={ref}
					type="button"
					disabled={isDisabled || loading}
					variant="outline"
					width="100%"
					opacity={isDisabled || loading ? 0.3 : 1}
					bg={isDisabled || loading ? 'white' : 'white'}
					_hover={{ bg: 'none' }}
					colorPalette="gray"
					onClick={handleToggle}
					borderColor={isInvalid ? 'red.500' : undefined}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-invalid={isInvalid || undefined}
					aria-required={isRequired || undefined}
					{...(name ? { name } : {})}
				>
					<Flex justifyContent="space-between" alignItems="center" width="100%">
						{loading ? 'Cargando...' : selectedLabel || placeholder}
						<Show when={!loading} fallback={<Spinner size="xs" />}>
							<Icon color="gray.500" w="14px" h="14px">
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
									<path d="M297.4 470.6C309.9 483.1 330.2 483.1 342.7 470.6L534.7 278.6C547.2 266.1 547.2 245.8 534.7 233.3C522.2 220.8 501.9 220.8 489.4 233.3L320 402.7L150.6 233.4C138.1 220.9 117.8 220.9 105.3 233.4C92.8 245.9 92.8 266.2 105.3 278.7L297.3 470.7z" />
								</svg>
							</Icon>
						</Show>
					</Flex>
				</Button>

				{isOpen && (
					<Box
						onMouseDown={(e) => e.preventDefault()}
						position="absolute"
						top="100%"
						left={0}
						right={0}
						zIndex={10}
						background="white"
						border="1px solid #ccc"
						borderRadius="4px"
						marginTop="2px"
						maxHeight="220px"
						display="flex"
						flexDirection="column"
					>
						<Input
							type="text"
							placeholder="Buscar..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							autoFocus
						/>
						<Box overflowY="auto" maxHeight="180px">
							{filteredOptions.length === 0 ? (
								<Box p="8px 10px" color="#999">
									Sin resultados
								</Box>
							) : (
								filteredOptions.map((option) => (
									<Box
										key={option.id}
										role="option"
										aria-selected={option.id === value}
										tabIndex={0}
										onClick={() => handleSelect(option.id)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') handleSelect(option.id);
										}}
										p="6px 10px"
										cursor="pointer"
										background={option.id === value ? '#f0f0f0' : 'white'}
										onMouseEnter={(e) => {
											(e.target as HTMLElement).style.background = '#f5f5f5';
										}}
										onMouseLeave={(e) => {
											(e.target as HTMLElement).style.background =
												option.id === value ? '#f0f0f0' : 'white';
										}}
									>
										{option.name}
									</Box>
								))
							)}
						</Box>
					</Box>
				)}
			</div>
		);
	},
);

CustomSelect.displayName = 'CustomSelect';
